/**
 * All user-facing strings.
 *
 * English is the only language at launch (decision 2026-07-26), so we ship no
 * i18n library — that saves roughly 15 KB for a feature nobody has asked for.
 * Centralising the strings anyway means adding Hindi later is a mechanical job:
 * add `hi.ts`, wire a lookup, done. Scattering them through JSX would make the
 * same change a rewrite.
 */
export const en = {
  common: {
    appName: 'TaskFlow',
    organisation: 'Cuddles Foundation',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    close: 'Close',
    retry: 'Try again',
    loading: 'Loading',
    search: 'Search',
    signOut: 'Sign out',
  },

  auth: {
    signInTitle: 'Sign in to TaskFlow',
    signInSubtitle: 'Internal project management for Cuddles Foundation',
    continueWithGoogle: 'Continue with Google',
    orDivider: 'or sign in with email',
    emailLabel: 'Email address',
    emailPlaceholder: 'you@cuddlesfoundation.com',
    passwordLabel: 'Password',
    rememberMe: 'Keep me signed in',
    forgotPassword: 'Forgot your password?',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    noAccountHelp: 'Accounts are created by an administrator. Ask your team lead for access.',

    forgotTitle: 'Reset your password',
    forgotSubtitle: 'We will email you a link to set a new password.',
    forgotSubmit: 'Send reset link',
    // Worded so it is true whether or not the address exists — telling someone "no
    // such account" would let anyone discover who works here.
    forgotSent:
      'If an account exists for that address, a reset link is on its way. Check your inbox.',
    backToSignIn: 'Back to sign in',

    resetTitle: 'Choose a new password',
    resetSubtitle: 'Pick something long. Length matters more than punctuation.',
    newPasswordLabel: 'New password',
    confirmPasswordLabel: 'Confirm new password',
    resetSubmit: 'Set new password',
    resetDone: 'Your password has been changed. Please sign in with it.',
    resetLinkInvalid: 'This reset link is invalid or has expired. Please request a new one.',
  },

  nav: {
    boards: 'Boards',
    search: 'Search',
    notifications: 'Notifications',
    settings: 'Settings',
    admin: 'Admin',
    uiKit: 'UI kit',
  },

  errors: {
    notFoundTitle: 'Page not found',
    notFoundBody: 'The page you were looking for does not exist or has moved.',
    backToBoards: 'Back to boards',
    boundaryTitle: 'Something went wrong',
    boundaryBody:
      'The screen failed to render. Reloading usually fixes it. If it keeps happening, send the reference below to your administrator.',
    reload: 'Reload the page',
  },

  theme: {
    label: 'Colour theme',
    light: 'Light',
    dark: 'Dark',
    system: 'System',
  },
} as const;

export type Messages = typeof en;
